/**
 * StageConversionReport — the COHORT conversion funnel (Reports → Conversion).
 *
 * Unlike FunnelReport (point-in-time occupancy), this answers: of the leads that
 * ENTERED the pipeline in the selected period, how many ever REACHED each stage
 * (a high-water mark, so a pivot moving a lead backward never un-reaches a
 * stage), what % converted from the previous stage, and the median time spent
 * in each stage. Sliceable overall / by study / by source / by recruiter, and
 * pinnable to one study (onlyGroupKey + studyId).
 */
import { useEffect, useMemo, useState } from 'react';
import api from '../../../services/api';
import { StageConversionRow } from '../../types';
import { LIFECYCLES } from '../../lib';
import { fmtDurationLong } from './StatBlocks';

interface Props {
  groupBy: 'overall' | 'study' | 'lead_source' | 'recruiter';
  from: string | null;
  to: string | null;
  /** When set, only this group's funnel renders (a study pin). */
  onlyGroupKey?: string | null;
  /** Scope the cohort to one study (current primary study). */
  studyId?: string | null;
  /** Optional recruiter pin (independent of group_by). */
  recruiter?: string | null;
}

const STAGE_COLORS: Record<string, string> = {
  new: 'bg-cyan-500',
  contacting: 'bg-blue-500',
  qualified_pending_records: 'bg-indigo-500',
  ready_to_schedule: 'bg-teal-500',
  scheduled: 'bg-amber-500',
  screened: 'bg-purple-500',
  randomized: 'bg-green-500',
};

interface Group {
  key: string;
  label: string;
  rows: StageConversionRow[]; // ordinal-ascending
  cohort: number; // reached at the base stage = leads that entered
}

/** Conversion-rate chip tone: healthy green / watch amber / weak rose. */
function convClass(pct: number): string {
  if (pct >= 60) return 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300';
  if (pct >= 30) return 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300';
  return 'bg-rose-100 text-rose-700 dark:bg-rose-900/30 dark:text-rose-300';
}

export default function StageConversionReport({
  groupBy,
  from,
  to,
  onlyGroupKey = null,
  studyId = null,
  recruiter = null,
}: Props) {
  const [rows, setRows] = useState<StageConversionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api
      .get('/api/crm/analytics/stage-conversion', {
        params: {
          group_by: groupBy,
          from: from || undefined,
          to: to || undefined,
          study_id: studyId || undefined,
          recruiter: recruiter || undefined,
        },
      })
      .then((res) => {
        if (alive) {
          setRows(res.data?.rows || []);
          setError(null);
        }
      })
      .catch((err) => {
        if (alive) setError(err?.response?.data?.error || 'Failed to load conversion');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [groupBy, from, to, studyId, recruiter]);

  const groups: Group[] = useMemo(() => {
    const byKey = new Map<string, Group>();
    for (const r of rows) {
      if (onlyGroupKey && r.group_key !== onlyGroupKey) continue;
      const g = byKey.get(r.group_key) || { key: r.group_key, label: r.group_label, rows: [], cohort: 0 };
      g.rows.push(r);
      if (Number(r.ordinal) === 1) g.cohort = Number(r.reached);
      byKey.set(r.group_key, g);
    }
    for (const g of byKey.values()) g.rows.sort((a, b) => Number(a.ordinal) - Number(b.ordinal));
    return [...byKey.values()].sort((a, b) => b.cohort - a.cohort);
  }, [rows, onlyGroupKey]);

  if (loading) return <p className="p-4 text-sm text-gray-400">Loading conversion…</p>;
  if (error) return <p className="p-4 text-sm text-red-600 dark:text-red-400">{error}</p>;
  if (groups.length === 0) {
    return (
      <p className="p-4 text-sm text-gray-500 dark:text-gray-400">
        No leads entered the pipeline in this range yet.
      </p>
    );
  }

  const renderGroup = (g: Group) => (
    <div key={g.key} className="rounded-xl border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="min-w-0 truncate text-sm font-semibold text-gray-900 dark:text-gray-100">{g.label}</h3>
        <span className="flex-shrink-0 text-xs text-gray-400">{g.cohort.toLocaleString()} entered</span>
      </div>
      <div className="space-y-2.5">
        {g.rows.map((r) => {
          const meta = LIFECYCLES.find((l) => l.value === r.stage);
          const sharePct = g.cohort > 0 ? Math.round((Number(r.reached) / g.cohort) * 100) : 0;
          const barPct = g.cohort > 0 ? Math.max(2, sharePct) : 0;
          const color = STAGE_COLORS[r.stage] || 'bg-gray-400';
          return (
            <div key={r.stage}>
              <div className="flex items-center justify-between gap-2 text-sm">
                <span className="min-w-0 truncate font-medium text-gray-700 dark:text-gray-200">{meta?.label || r.stage}</span>
                <span className="flex flex-shrink-0 items-center gap-2 tabular-nums">
                  {r.conversion_pct !== null && Number(r.ordinal) > 1 && (
                    <span
                      className={`rounded-full px-1.5 py-0.5 text-[10px] font-medium ${convClass(Number(r.conversion_pct))}`}
                      title="Converted from the previous stage"
                    >
                      {Math.round(Number(r.conversion_pct))}%
                    </span>
                  )}
                  <span className="text-gray-700 dark:text-gray-200">{Number(r.reached).toLocaleString()}</span>
                  <span className="w-9 text-right text-xs text-gray-400">{sharePct}%</span>
                </span>
              </div>
              <div className="mt-1 h-2 overflow-hidden rounded-full bg-gray-100 dark:bg-gray-700">
                <div className={`h-full rounded-full ${color} transition-all`} style={{ width: `${barPct}%` }} />
              </div>
              {(r.median_dwell_sec !== null || Number(r.censored) > 0 || Number(r.reentry) > 0) && (
                <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 px-0.5 text-[11px] text-gray-400">
                  {r.median_dwell_sec !== null && <span>~{fmtDurationLong(Number(r.median_dwell_sec))} in stage</span>}
                  {Number(r.censored) > 0 && <span>{Number(r.censored)} still here</span>}
                  {Number(r.reentry) > 0 && <span>{Number(r.reentry)} re-entered</span>}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <p className="mt-3 text-[11px] leading-snug text-gray-400">
        Each lead is counted at the furthest stage it ever reached. The chip is the % that converted from the previous
        stage; time-in-stage is the median, excluding leads still in the stage. Leads that entered before stage
        tracking shipped show only their furthest stage, so intermediate conversion can read high for older ranges.
      </p>
    </div>
  );

  if (groupBy === 'overall' || onlyGroupKey) {
    return <div className="max-w-xl">{groups.map(renderGroup)}</div>;
  }
  return <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">{groups.map(renderGroup)}</div>;
}
